import { useState, useEffect } from "react";
import { Transaction } from "../../../services/api";
import { getReviewByEntity } from "../../data/api/reviewsApi";
import { getUser } from "../../data/storage/authStorage";

export interface BankTransactionReviewDataState {
  review: any | null; // Review for current user (for logic checks)
  reviewForPopulation: any | null; // Review for auto-population (regardless of user_id)
  isLoading: boolean;
  reviewStatus: "pending" | "approved" | "rejected" | null;
  hasReview: boolean;
  error: Error | null;
}

export const useBankTransactionReviewData = (
  transactionData: Transaction | null
): BankTransactionReviewDataState => {
  const [review, setReview] = useState<any | null>(null);
  const [reviewForPopulation, setReviewForPopulation] = useState<any | null>(
    null
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [reviewStatus, setReviewStatus] = useState<
    "pending" | "approved" | "rejected" | null
  >(null);
  const [hasReview, setHasReview] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!transactionData?.id) {
      setReview(null);
      setReviewStatus(null);
      setHasReview(false);
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    const fetchReview = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await getReviewByEntity(
          "bank_transaction",
          transactionData.id!
        );

        if (!isMounted) return;

        const reviewData = response.data?.review || null;

        if (reviewData) {
          // Get current user ID
          const currentUser = getUser();
          const currentUserIdStr = currentUser?.id;
          const currentUserId: number | null = currentUserIdStr
            ? parseInt(currentUserIdStr, 10)
            : null;

          // Always show review status regardless of user_id (for banner display)
          setReviewStatus(reviewData.status || null);

          // Always set reviewForPopulation for auto-population (regardless of user_id)
          setReviewForPopulation(reviewData);

          // Only set review and hasReview if user_id matches (for logic checks in handleMatch)
          if (
            currentUserId !== null &&
            !isNaN(currentUserId) &&
            reviewData.user_id === currentUserId
          ) {
            setReview(reviewData);
            setHasReview(true);
          } else {
            setReview(null);
            setHasReview(false);
          }
        } else {
          setReview(null);
          setReviewForPopulation(null);
          setReviewStatus(null);
          setHasReview(false);
        }
      } catch (err) {
        console.error("Error fetching review:", err);
        if (isMounted) {
          setError(
            err instanceof Error ? err : new Error("Failed to fetch review")
          );
          setReview(null);
          setReviewForPopulation(null);
          setReviewStatus(null);
          setHasReview(false);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchReview();

    return () => {
      isMounted = false;
    };
  }, [transactionData?.id]);

  return {
    review,
    reviewForPopulation,
    isLoading,
    reviewStatus,
    hasReview,
    error,
  };
};
